import React, { useEffect, useRef } from 'react';
import { applyLicenseKey } from '../services/LicenseService';

/**
 * Handles the redirect landing from the trial-welcome email's verification
 * link: the Licensing Server verifies the email server-side, then sends the
 * browser here with ?licenseKey=... so this app can apply that license and
 * open the unified sign-in dialog with the license already in place, instead
 * of requiring a brand-new trial user to manually re-enter the key they were
 * just emailed.
 *
 * @returns {@code null} — renders nothing, exists for its side effects only.
 */
export const VerificationLandingHandler: React.FC = () => {
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    const params = new URLSearchParams(window.location.search);
    const licenseKey = params.get('licenseKey');
    if (!licenseKey) return;
    handled.current = true;

    // Strip the param immediately so a refresh or back-navigation doesn't re-trigger this.
    const url = new URL(window.location.href);
    url.searchParams.delete('licenseKey');
    window.history.replaceState({}, '', url.toString());

    (async () => {
      const result = await applyLicenseKey(licenseKey);
      if (result.success) {
        // Landing here means the Licensing Server just verified the email.
        // Tell the Getting Started guide — in this tab directly, and in any
        // other same-browser tab via the storage event.
        window.dispatchEvent(new CustomEvent('pfb-email-verified'));
        try {
          localStorage.setItem('pfbEmailVerifiedAt', String(Date.now()));
        } catch { /* storage unavailable — the guide's manual confirmation covers it */ }

        // App.tsx listens for this and opens the unified sign-in dialog,
        // which will show the license as active and ask for credentials.
        window.dispatchEvent(new CustomEvent('open-signin-dialog'));
      }
    })();
  }, []);

  return null;
};

export default VerificationLandingHandler;
